import { useEffect, useState } from 'react'

import { Section, Text } from '@factorialco/deprecated-design-system'
import {
  SalaryAdvanceSettingsPageDocument,
  SaveSalaryAdvanceCompanySettingDocument,
  type SaveSalaryAdvanceCompanySettingMutation,
} from 'generated/resources/graphql'

import { SingleRangeSlider } from 'modules/core/components/SingleRangeSlider'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type Props = {
  companyId: number
  currentCapPercentage: number | null | undefined
  requestDeadlineDay: number
  canEdit: boolean
}

const CapPercentageSetting = ({ companyId, currentCapPercentage, requestDeadlineDay, canEdit }: Props) => {
  const [selectedCapPercentage, setSelectedCapPercentage] = useState<number | null>(currentCapPercentage ?? null)

  const [saveCompanySetting, { loading }] = useMutation(SaveSalaryAdvanceCompanySettingDocument, {
    extractorFn: (payload: SaveSalaryAdvanceCompanySettingMutation) =>
      payload.salaryAdvanceMutations?.saveSalaryadvanceCompanySetting ?? { errors: [] },
    refetchQueries: [
      {
        query: SalaryAdvanceSettingsPageDocument,
        variables: { companyId },
      },
    ],
  })

  useEffect(() => {
    setSelectedCapPercentage(currentCapPercentage ?? null)
  }, [currentCapPercentage])

  const isDirty = selectedCapPercentage !== currentCapPercentage

  const handleSave = async () => {
    const result = await saveCompanySetting({
      variables: {
        companyId,
        advanceCapMonthlyWagePercent: selectedCapPercentage,
        requestDeadlineDay,
      },
    })
    const { success } = handleResult(result, () => {})

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  return (
    <Section.Form
      illustration="money-bag"
      title={i18n.t('salary_advance.settings.cap_percentage.title')}
      description={i18n.t('salary_advance.settings.cap_percentage.description')}
      submitLabel={i18n.t('common.save')}
      isDirty={isDirty}
      onSubmit={handleSave}
      disabled={!canEdit || loading}
    >
      <Text.Normal weight="semibold" size="200">
        {i18n.t('salary_advance.settings.cap_percentage.label', {
          percentage: selectedCapPercentage ?? 0,
        })}
      </Text.Normal>
      <SingleRangeSlider
        min={0}
        max={100}
        value={selectedCapPercentage ?? 0}
        onChange={setSelectedCapPercentage}
        readonly={!canEdit}
      />
    </Section.Form>
  )
}

export default CapPercentageSetting
